import React, { useState } from 'react';

import type { FileTypeFilterOption } from '@aline/common';
import { FILE_TYPE_FILTER_OPTIONS } from '@aline/common';
import { Autocomplete, TextField, Typography } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { AlineCheckbox, FileTypeIcon, FilterTags } from 'src/components';
import { ContractIcon } from 'src/components/icons/ContractIcon';
import { makeStyles } from 'tss-react/mui';

import {
  filterAutocompleteStyle,
  usePreserveAutocompleteSearchInput,
} from './autocomplete-filter-utils';

const OPTION_ICON_PX = 20;

const useStyles = makeStyles()(theme => ({
  autocomplete: filterAutocompleteStyle,
  iconWrap: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    height: OPTION_ICON_PX,
    justifyContent: 'center',
    width: OPTION_ICON_PX,
  },
  item: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

const optionIconProps = { height: OPTION_ICON_PX, width: OPTION_ICON_PX };

export function FileTypeOptionIcon({
  className,
  option,
}: {
  className: string;
  option: FileTypeFilterOption;
}): JSX.Element {
  if (!option.mimetype)
    return (
      <span className={className}>
        <ContractIcon {...optionIconProps} />
      </span>
    );

  return (
    <span className={className}>
      <FileTypeIcon iconProps={optionIconProps} mimetype={option.mimetype} />
    </span>
  );
}

export function FileTypeAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const { classes } = useStyles();

  const [truncateTags, setTruncateTags] = useState(true);
  const [values, setValues] = useState<FileTypeFilterOption[]>(item.value ?? []);
  const { inputValue, onInputChange } = usePreserveAutocompleteSearchInput();

  function handleValueChange(value: FileTypeFilterOption[]): void {
    applyValue({
      ...item,
      value,
    });
    setValues(value);
  }

  return (
    <Autocomplete<FileTypeFilterOption, true, false, false>
      disableCloseOnSelect
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={false}
      className={classes.autocomplete}
      getOptionLabel={option => option.label}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => option.label === value.label}
      options={FILE_TYPE_FILTER_OPTIONS}
      renderInput={params => {
        return <TextField placeholder="Search file types..." {...params} variant="standard" />;
      }}
      renderOption={(props, option, state) => {
        const { className, key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps} className={clsx(className, classes.item)}>
            <AlineCheckbox checked={state.selected} />
            <FileTypeOptionIcon className={classes.iconWrap} option={option} />
            <Typography>{option.label}</Typography>
          </li>
        );
      }}
      renderValue={tags => {
        return (
          <FilterTags<FileTypeFilterOption>
            getTextFromTag={tag => tag.label}
            tags={tags}
            truncateTags={truncateTags}
            onDelete={tag => handleValueChange(tags.filter(filterTag => tag !== filterTag))}
          />
        );
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              enabled: false,
              name: 'flip',
            },
            {
              enabled: false,
              name: 'preventOverflow',
            },
          ],
        },
      }}
      value={values}
      onBlur={e => {
        e.stopPropagation();
        setTruncateTags(true);
      }}
      onChange={(_e, values) => handleValueChange(values)}
      onFocus={e => {
        e.stopPropagation();
        setTruncateTags(false);
      }}
      onInputChange={onInputChange}
    />
  );
}
